import { ArrowRight } from "lucide-react";
import type React from "react";

import { cn } from "~/lib/utils";

/**
 * Inline diagrams for editorial content.
 *
 * The governing rule, and the only one that matters: a diagram earns its place
 * only when it holds information that prose holds worse. Two shapes clear that
 * bar for this kind of writing.
 *
 * `Timeline` holds a sequence with quantity. A deadline has extent and stakes,
 * and you can see six months running out in a way a sentence cannot show.
 *
 * `Comparison` holds a paired correction. Two columns are scannable in a way
 * prose is not, and the asymmetry between a belief and its correction does
 * emotional work.
 *
 * Three earlier components were cut for failing the rule, and the reasons are
 * worth keeping so they do not get rebuilt. `DecisionFork` drew two branch cards
 * under a question, but stacked branches are just a list and the branching
 * geometry carried no information. `Spectrum` answered "how bad is it?" by
 * refusing to give a threshold, and a refusal is a sentence, not a picture:
 * drawing a bar to announce it has no divisions is self-defeating. `Cycle`
 * numbered the steps of a loop, which drains the rhythm prose gives it for free.
 *
 * Implementation notes. Props are data, so a pre-save gate can assert a step has
 * a label or that a cited source resolves. Colour comes from brand tokens rather
 * than literals. Both stack correctly at 375px, where most of the audience reads.
 * Each has exactly one typographically large element, because a diagram whose
 * text is all one size explains nothing at a glance. No gradients anywhere.
 */

interface DiagramSource {
  label: string;
  href?: string;
}

/** Small uppercase eyebrow plus optional citation. Deliberately not a header band. */
function Eyebrow({
  title,
  source,
}: {
  title?: string;
  source?: DiagramSource;
}) {
  if (!title && !source) return null;
  return (
    <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      {title && (
        <figcaption className="text-xs font-semibold uppercase tracking-[0.09em] text-muted-foreground">
          {title}
        </figcaption>
      )}
      {source && (
        <span className="text-xs text-muted-foreground/80">
          {source.href ? (
            <a
              href={source.href}
              className="underline decoration-border underline-offset-2 hover:decoration-brand"
            >
              {source.label}
            </a>
          ) : (
            source.label
          )}
        </span>
      )}
    </div>
  );
}

/** The one sentence the reader should leave with. Sits outside the diagram body. */
function Takeaway({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 mt-4 border-t border-border pt-3 text-sm leading-relaxed text-foreground">
      {children}
    </p>
  );
}

const FIGURE = "my-7 md:my-9";

/* ------------------------------------------------------------------ Timeline */

export interface TimelineStep {
  label: string;
  note?: string;
  /**
   * `point` is a moment. `span` is a duration the reader has to act inside, and
   * gets visible extent plus large type, because the duration is usually the
   * whole reason the diagram exists. `branch` only starts if something else
   * happened first, so it is drawn detached.
   */
  kind?: "point" | "span" | "branch";
}

export const Timeline = ({
  title,
  steps,
  takeaway,
  source,
}: {
  title?: string;
  steps: TimelineStep[];
  takeaway?: string;
  source?: DiagramSource;
}) => (
  <figure className={FIGURE}>
    <Eyebrow title={title} source={source} />

    <ol className="m-0 list-none p-0">
      {steps.map((step, i) => {
        const kind = step.kind ?? "point";
        const isLast = i === steps.length - 1;

        return (
          <li key={`${step.label}-${i}`} className="flex gap-4">
            {/*
              One rail weight throughout. An earlier version thickened the rail
              from 1px to 6px for a span, which read as a rendering glitch rather
              than emphasis, and made the 14px dots look heavy against a hairline.
              Colour now carries the distinction and the large type carries the
              emphasis. Each dot is nudged to sit against its own line's optical
              centre, since a 28px label and a 15px label do not align to the same
              offset.
            */}
            <div className="flex w-3 shrink-0 flex-col items-center">
              <span
                className={cn(
                  "h-2.5 w-2.5 shrink-0 rounded-full",
                  // Aligned to each line's optical centre. A 28px span label and a
                  // 15px point label do not share an offset, so the dot is nudged
                  // per kind rather than using one value for both.
                  kind === "span" ? "mt-[7px]" : "mt-[5px]",
                  kind === "branch"
                    ? "border-[1.5px] border-brand bg-background"
                    : "bg-brand",
                )}
              />
              {!isLast && (
                <span
                  className={cn(
                    // No vertical margin: the rail has to touch the dots at both
                    // ends. With margins it floated in the gap and read as a
                    // broken line rather than a connection.
                    "w-0.5 flex-1",
                    kind === "span" ? "bg-brand" : "bg-border",
                  )}
                />
              )}
            </div>

            <div className={cn("min-w-0 flex-1", isLast ? "pb-0" : "pb-7")}>
              {kind === "span" ? (
                <p
                  className="m-0 text-2xl font-bold leading-none tracking-tight text-brand md:text-[28px]"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {step.label}
                </p>
              ) : (
                <p className="m-0 text-[15px] font-semibold leading-snug text-foreground md:text-base">
                  {step.label}
                </p>
              )}
              {step.note && (
                <p className="m-0 mt-1.5 max-w-[52ch] text-sm leading-relaxed text-muted-foreground">
                  {step.note}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>

    {takeaway && <Takeaway>{takeaway}</Takeaway>}
  </figure>
);

/* ---------------------------------------------------------------- Comparison */

/**
 * Two-sided contrast, and the only component for it. The left column is what the
 * reader observes or tells themselves; the right is what it means or the
 * correction. The right side is set confidently and the left quietly, because a
 * comparison where both sides look equally true corrects nothing.
 *
 * This also absorbs the condition-to-consequence job. An earlier `DecisionFork`
 * drew two branch cards under a question; it was deleted because stacked branches
 * are just a list, its connector lines broke at mobile width, and the branching
 * geometry added no information a reader could use.
 */
export const Comparison = ({
  columns,
  rows,
  title,
  takeaway,
  source,
}: {
  columns: [string, string];
  rows: Array<{ left: string; right: string }>;
  title?: string;
  takeaway?: string;
  source?: DiagramSource;
}) => (
  <figure className={FIGURE}>
    <Eyebrow title={title} source={source} />

    <ul className="m-0 list-none divide-y divide-border border-y border-border p-0">
      {rows.map((r, i) => (
        <li
          key={`${r.left}-${i}`}
          className="grid items-start gap-2 py-4 md:grid-cols-[1fr_auto_1fr] md:gap-5"
        >
          <div>
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground/70">
              {columns[0]}
            </span>
            <p className="m-0 text-sm italic leading-relaxed text-muted-foreground">
              {r.left}
            </p>
          </div>

          <ArrowRight
            className="hidden h-4 w-4 shrink-0 self-center text-border md:block"
            aria-hidden="true"
          />

          <div>
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.09em] text-brand/70">
              {columns[1]}
            </span>
            <p className="m-0 text-[15px] font-medium leading-relaxed text-foreground">
              {r.right}
            </p>
          </div>
        </li>
      ))}
    </ul>

    {takeaway && <Takeaway>{takeaway}</Takeaway>}
  </figure>
);
